import { useNavigate } from '@tanstack/react-router'
import sortBy from 'lodash/sortBy'
import { type ElementType, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import MarqueeModule, { type MarqueeProps } from 'react-fast-marquee'
import { useListOpenOrderBooks } from '@/hooks/useListOpenOrderBooks'
import { type AssetPrice, couponKind, useGetAllAssetPrices, useListAssets } from '@/hooks/v1'
import { formatNumber, parseFiniteNumber } from '@/utils/numberUtils'

const Marquee = ((MarqueeModule as unknown as { default?: ElementType<MarqueeProps> }).default ??
  MarqueeModule) as ElementType<MarqueeProps>

type AssetPriceMarqueeItem = {
  symbol: string
  price: number
  ytm?: number
  ytmDirection?: 'up' | 'down' | 'same'
  assetID: string
}

const getYtmColorClassName = (direction: AssetPriceMarqueeItem['ytmDirection']) => {
  if (direction === 'up') return 'text-success'
  if (direction === 'down') return 'text-danger'
  return 'text-white'
}

export const AssetPriceMarquee = () => {
  const navigate = useNavigate()
  const { data: allPrices, isLoading: loadingPrices } = useGetAllAssetPrices()
  const prices = useMemo(() => {
    if (allPrices?.data && Array.isArray(allPrices.data)) {
      return allPrices.data
    }
    return []
  }, [allPrices])
  const { data: allAssetsData } = useListAssets()
  const allAssets = useMemo(() => allAssetsData?.data ?? [], [allAssetsData?.data])
  const { data: orderBooksResponse, isLoading: loadingOrderBooks } = useListOpenOrderBooks()
  const orderBooks = useMemo(() => orderBooksResponse?.data ?? [], [orderBooksResponse?.data])
  const visibleOrderBookAssetIDs = useMemo(
    () =>
      new Set(
        orderBooks.flatMap((orderBook) => [orderBook.base_asset_id, orderBook.quote_asset_id]),
      ),
    [orderBooks],
  )
  const [marqueeItems, setMarqueeItems] = useState<AssetPriceMarqueeItem[]>([])
  const previousYtmByAssetIDRef = useRef<Map<string, number>>(new Map())
  const [shouldScroll, setShouldScroll] = useState(false)

  useEffect(() => {
    const updateMarqueeItems = (currentPrices: AssetPrice[]) => {
      if (!currentPrices || currentPrices.length === 0) {
        setMarqueeItems([])
        return
      }
      const previousYtmByAssetID = previousYtmByAssetIDRef.current
      const nextYtmByAssetID = new Map<string, number>()

      const newItems = sortBy(
        currentPrices
          .filter((item) => {
            const asset = allAssets.find((asset) => asset.id === item.asset_id)
            return (
              item.price !== null &&
              item.price !== undefined &&
              item.asset_id !== undefined &&
              visibleOrderBookAssetIDs.has(item.asset_id) &&
              asset?.bond?.coupon_kind === couponKind['COUPON-PAYING']
            )
          })
          .map((item) => {
            const asset = allAssets.find((asset) => asset.id === item.asset_id)
            const assetID = item.asset_id!
            const ytm = parseFiniteNumber(item.ytm) ?? undefined
            const previousYtm = previousYtmByAssetID.get(assetID)
            const ytmDirection: AssetPriceMarqueeItem['ytmDirection'] =
              ytm === undefined || previousYtm === undefined
                ? 'same'
                : ytm > previousYtm
                  ? 'up'
                  : ytm < previousYtm
                    ? 'down'
                    : 'same'

            if (ytm !== undefined) {
              nextYtmByAssetID.set(assetID, ytm)
            }

            return {
              assetID,
              price: Number.parseFloat(item.price!),
              symbol: asset?.symbol || assetID,
              ytm,
              ytmDirection,
            }
          }),
        'symbol',
        'asc',
      )

      previousYtmByAssetIDRef.current = nextYtmByAssetID
      setMarqueeItems(newItems)
    }
    if (Array.isArray(prices)) {
      updateMarqueeItems(prices)
    }
  }, [prices, allAssets, visibleOrderBookAssetIDs])

  const observePriceRow = useCallback((row: HTMLDivElement | null) => {
    const container = row?.closest('[data-price-marquee]')
    // The animated marquee can render multiple copies of the row.
    if (!row || !container || container.querySelector('[data-price-row]') !== row) return

    const updateShouldScroll = () => {
      setShouldScroll(row.getBoundingClientRect().width > container.getBoundingClientRect().width)
    }

    updateShouldScroll()
    const observer = new ResizeObserver(updateShouldScroll)
    observer.observe(container)
    observer.observe(row)
    return () => observer.disconnect()
  }, [])

  if ((loadingPrices || loadingOrderBooks) && marqueeItems.length === 0) {
    return (
      <div className="flex-1 min-w-0 bg-panel py-1 px-4 text-xs text-muted-foreground">
        Loading prices...
      </div>
    )
  }

  if (marqueeItems.length === 0) {
    return null
  }

  const navigateToAssetTrade = (assetID: string) => {
    const navigateOrderbook = orderBooks.find(
      (pool) => pool.base_asset_id === assetID || pool.quote_asset_id === assetID,
    )
    if (!navigateOrderbook) return
    navigate({ to: `/trade/${navigateOrderbook.order_book_id}` })
  }

  const content = (
    <div
      ref={observePriceRow}
      data-price-row
      className="flex w-max shrink-0 items-center whitespace-nowrap"
    >
      {marqueeItems.map((item: AssetPriceMarqueeItem, index: number) => {
        const isLastItem = index === marqueeItems.length - 1

        return (
          <div key={`${item.assetID}-${item.symbol}`} className="relative flex items-center">
            <button
              type="button"
              className="px-4 text-sm cursor-pointer group bg-transparent border-0 p-0"
              onClick={() => {
                navigateToAssetTrade(item.assetID)
              }}
            >
              <span className="font-bold text-white group-hover:text-primary">{item.symbol}: </span>
              <span className="text-muted-foreground text-white-dark">
                ${formatNumber(item.price, 3)}
              </span>
              {item.ytm !== undefined ? (
                <span className={getYtmColorClassName(item.ytmDirection)}>
                  {' '}
                  - YTM {formatNumber(item.ytm, 2)}%
                </span>
              ) : null}
            </button>
            {(!isLastItem || shouldScroll) && (
              <div className={`h-4 w-px bg-gray-600${isLastItem ? ' absolute right-0' : ''}`} />
            )}
          </div>
        )
      })}
    </div>
  )

  return (
    <div data-price-marquee className="relative flex min-w-0 flex-1 overflow-hidden bg-panel py-1">
      {shouldScroll && (
        <>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 z-10 w-6"
            style={{ background: 'linear-gradient(to right, var(--color-panel), transparent)' }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-0 z-10 w-4"
            style={{ background: 'linear-gradient(to left, var(--color-panel), transparent)' }}
          />
        </>
      )}
      {shouldScroll ? (
        <Marquee play pauseOnHover autoFill gradient={false}>
          {content}
        </Marquee>
      ) : (
        content
      )}
    </div>
  )
}
